import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLang } from "../../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, FileText } from "lucide-react";
import { BackBar } from "../../components/BackBar";

const ELEM_GRADES = ["gs", "cp", "ce1", "ce2", "cm1", "cm2", "k", "kindergarten", "grade 1", "grade 2", "grade 3", "grade 4", "grade 5", "1st", "2nd", "3rd", "4th", "5th"]; // eslint-disable-line no-unused-vars

// Compute deposit (tiered): 295€ for 1 course, 390€ for 2 courses.
function depositFor(courseCount) {
  return courseCount <= 1 ? 295 : 390;
}

// Build the 3 payment-plan options for the BALANCE (annual total - deposit).
// Dates are FIXED on the school-year calendar.
// 1× = 15 août · 2× = 15 août + 15 janvier · 10× = 15 août → 15 mai
function modesForBalance(balance, lang) {
  const fmt = (n) => Math.round(n).toLocaleString("fr-FR");
  const t = lang === "fr";
  return [
    { key: "annual",   price: `${fmt(balance)} € × 1`,        hint: t ? "le 15 août" : "on Aug 15" },
    { key: "biannual", price: `${fmt(balance / 2)} € × 2`,    hint: t ? "15 août & 15 janvier" : "Aug 15 & Jan 15" },
    { key: "monthly",  price: `${fmt(balance / 10)} € × 10`,  hint: t ? "le 15 du mois, août → mai" : "15th of each month, Aug → May" },
    { key: "custom",   price: "—",                            hint: "" },
  ];
}

export default function EnrollmentPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [app, setApp] = useState(null);
  const [signed, setSigned] = useState(false);
  const [mode, setMode] = useState("annual");
  const [tz, setTz] = useState("Europe/Paris");
  const [submitting, setSubmitting] = useState(false);
  const [showFullContract, setShowFullContract] = useState(false);
  const [courseCount, setCourseCount] = useState(1);
  const [catalog, setCatalog] = useState(null);  // {eligible, courses, track, grade_display}
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);

  useEffect(() => {
    // The :id route param can be EITHER an application_id (new enrollment flow)
    // OR an enrollment_id coming from the reenrollment "Resume" button. Try both.
    const loadFromApplication = async () => {
      const r = await api.get(`/applications/${id}`);
      setApp(r.data);
      return r.data;
    };
    const loadFromEnrollment = async () => {
      const r = await api.get(`/enrollments/${id}`);
      const enr = r.data;
      // Build a pseudo-application object so the rest of the page renders normally.
      const student = enr.student || {};
      setApp({
        id: enr.application_id || enr.id,
        student_id: enr.student_id,
        // expose info expected downstream
        student_first_name: student.first_name,
        student_last_name: student.last_name,
        grade: student.grade,
        from_reenrollment: true,
        enrollment_id: enr.id,
      });
      return enr;
    };

    (async () => {
      let data;
      try { data = await loadFromApplication(); }
      catch {
        try { data = await loadFromEnrollment(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); return; }
      }
      const sid = data.student_id;
      if (sid) {
        try {
          const d = await api.get(`/students/${sid}/diploma`);
          setCourseCount(1 + (d.data.enrolled_count || 0));
        } catch { setCourseCount(1); }
      }
      try {
        // Course catalog uses application id; in the reenrollment case we may not have one,
        // so fall back to "eligible:false" silently.
        const c = await api.get(`/applications/${data.id}/course-catalog`);
        setCatalog(c.data);
        if (c.data.eligible && c.data.courses?.length > 0) setCourseCount(1);
      } catch { setCatalog({ eligible: false, courses: [] }); }
    })();
  }, [id]);

  const toggleCourse = (cid) => {
    setSelectedCourseIds(prev => {
      const next = prev.includes(cid)
        ? prev.filter(x => x !== cid)
        : (prev.length < 2 ? [...prev, cid] : prev);  // cap at 2
      setCourseCount(Math.max(1, next.length));
      return next;
    });
  };

  if (!app) return <div className="p-12">{t("common.loading")}</div>;

  const annualPrice = 1995;

  // Financial breakdown (deposit: 295€ for 1 course / 390€ for 2 courses + balance via plan)
  const deposit = depositFor(courseCount);
  const annualTotal = annualPrice * courseCount;
  const balance = annualTotal - deposit;
  const MODES = modesForBalance(balance, lang);
  const balanceInstallment =
    mode === "annual" ? balance :
    mode === "biannual" ? balance / 2 :
    mode === "monthly" ? balance / 10 :
    balance;
  const installmentCount = mode === "annual" ? 1 : mode === "biannual" ? 2 : 10;
  const fmt = (n) => n.toLocaleString("fr-FR");

  const submit = async () => {
    if (!signed) { toast.error(t("contract.signature")); return; }
    if (catalog?.eligible && selectedCourseIds.length < 1) {
      toast.error(lang === "fr"
        ? "Veuillez choisir au moins 1 cours."
        : "Please choose at least 1 course.");
      return;
    }
    setSubmitting(true);
    let enrollmentId = null;
    try {
      if (app.from_reenrollment) {
        // Re-enrollment: the enrollment already exists. Just update payment_mode +
        // course selection then fire the deposit checkout straight away.
        enrollmentId = app.enrollment_id;
        await api.patch(`/enrollments/${enrollmentId}`, {
          payment_mode: mode,
          agreement_signed: signed,
          selected_course_ids: catalog?.eligible ? selectedCourseIds : [],
          schedule_preferences: { preferred_timezone: tz },
        }).catch(() => null);
      } else {
        // 1. Create enrollment record (auto-assigns English course OR uses selected courses)
        const { data: enrollment } = await api.post("/enrollments", {
          application_id: id, agreement_signed: signed, payment_mode: mode,
          schedule_preferences: { preferred_timezone: tz },
          selected_course_ids: catalog?.eligible ? selectedCourseIds : [],
        });
        enrollmentId = enrollment.id;
      }
      // 2. Trigger Stripe DEPOSIT checkout (295€ for 1 course / 390€ for 2 courses)
      const originUrl = window.location.origin;
      const { data: checkout } = await api.post(
        `/enrollments/${enrollmentId}/deposit/checkout`,
        { enrollment_id: enrollmentId, origin_url: originUrl },
      );
      if (checkout?.url) {
        window.location.href = checkout.url;
        return;
      }
      throw new Error("Stripe — aucune URL reçue");
    } catch (e) {
      toast.error(
        (enrollmentId ? "Inscription enregistrée, mais paiement de l'acompte non initié. Réessayez ou contactez-nous. · " : "") +
        (formatApiErrorDetail(e.response?.data?.detail) || e.message)
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-12 py-8" data-testid="enrollment-page">
      <BackBar to="/parent" fallbackLabelFr="Tableau de bord" fallbackLabelEn="Dashboard" />
      <p className="overline mb-2">{t("parent.myEnrollment")}</p>
      <h1 className="font-display font-bold text-4xl tracking-tighter mb-8">
        {app.student_info?.first_name} {app.student_info?.last_name}
      </h1>

      {/* Course picker — 3e → Terminale only */}
      {catalog?.eligible && (
        <Card className="mb-6 border-primary/30" data-testid="course-picker">
          <CardHeader>
            <CardTitle className="font-display tracking-tight text-2xl">
              {lang === "fr" ? "Choisissez vos cours" : "Choose your courses"}
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              {lang === "fr"
                ? `Niveau ${catalog.grade_display} · Parcours ${catalog.track === "fluent" ? "Fluent" : catalog.track === "bridge" ? "Bridge" : "à confirmer"} — choisissez 1 ou 2 cours (${selectedCourseIds.length}/2 sélectionné${selectedCourseIds.length > 1 ? "s" : ""})`
                : `Grade ${catalog.grade_display} · ${catalog.track === "fluent" ? "Fluent" : catalog.track === "bridge" ? "Bridge" : "to confirm"} Track — pick 1 or 2 courses (${selectedCourseIds.length}/2 selected)`}
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {catalog.courses.length === 0 && (
              <p className="text-sm text-muted-foreground py-2">
                {lang === "fr"
                  ? "Aucun cours encore disponible pour ce niveau. Contactez admissions@ofalycee.fr."
                  : "No courses available yet for this grade. Contact admissions@ofalycee.fr."}
              </p>
            )}
            {catalog.courses.map(c => {
              const checked = selectedCourseIds.includes(c.id);
              const disabled = !checked && selectedCourseIds.length >= 2;
              return (
                <label
                  key={c.id}
                  data-testid={`course-option-${c.id}`}
                  className={`flex items-start justify-between gap-3 border rounded-lg p-4 cursor-pointer transition-colors ${
                    checked ? "border-primary bg-primary/[0.05]" :
                    disabled ? "border-border opacity-50 cursor-not-allowed" :
                    "border-border hover:bg-secondary/40"
                  }`}
                >
                  <div className="flex items-start gap-3 flex-1">
                    <Checkbox
                      checked={checked}
                      disabled={disabled}
                      onCheckedChange={() => !disabled && toggleCourse(c.id)}
                      data-testid={`course-checkbox-${c.id}`}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{lang === "fr" ? c.name_fr : c.name_en}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {c.teacher} · {c.day} {c.time}
                        {c.format === "2x45" && c.day2 && c.time2 ? ` + ${c.day2} ${c.time2}` : ""}
                        {" "}({c.timezone})
                      </div>
                      {c.description && (
                        <div className="text-xs text-muted-foreground mt-1 leading-snug">{c.description}</div>
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-accent tabular-nums whitespace-nowrap">
                    1 995 €/an
                  </span>
                </label>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Contract */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="font-display tracking-tight text-2xl flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            {t("contract.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground leading-relaxed">{t("contract.preamble")}</p>
          {!showFullContract ? (
            <button data-testid="show-contract" onClick={() => setShowFullContract(true)} className="text-sm text-primary font-semibold inline-flex items-center gap-1 hover:underline">
              {t("contract.readFull")} <ChevronDown className="w-4 h-4" />
            </button>
          ) : (
            <div data-testid="full-contract" className="border rounded-lg p-5 bg-secondary/30 space-y-4 max-h-96 overflow-y-auto">
              {["art1", "art2", "art3", "art4", "art5", "art6", "art7"].map(art => (
                <div key={art}>
                  <div className="text-sm font-semibold text-foreground mb-1">{t(`contract.${art}Title`)}</div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{t(`contract.${art}`)}</p>
                </div>
              ))}
              <button onClick={() => setShowFullContract(false)} className="text-sm text-primary font-semibold inline-flex items-center gap-1 hover:underline">
                {t("contract.hideFull")} <ChevronUp className="w-4 h-4" />
              </button>
            </div>
          )}
          <div className="text-xs text-muted-foreground bg-secondary/40 p-3 rounded">{t("contract.signature")}</div>
          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox data-testid="enroll-sign" checked={signed} onCheckedChange={(v) => setSigned(!!v)} />
            <span className="text-sm font-medium">{t("parent.signAgreement")}</span>
          </label>
        </CardContent>
      </Card>

      {/* Payment */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="font-display tracking-tight text-2xl">{t("parent.choosePayment")}</CardTitle>
          <div className="text-xs text-muted-foreground mt-1">
            {lang === "fr"
              ? <>{annualPrice.toLocaleString("fr-FR")} € / cours / an · acompte {fmt(deposit)} € déjà déduit · <strong>solde à régler : {fmt(balance)} €</strong></>
              : <>€{annualPrice.toLocaleString("fr-FR")} / course / year · deposit €{fmt(deposit)} already deducted · <strong>balance to pay: €{fmt(balance)}</strong></>}
          </div>
        </CardHeader>
        <CardContent>
          <RadioGroup value={mode} onValueChange={setMode} className="space-y-3">
            {MODES.map(m => (
              <label key={m.key} className="flex items-center justify-between border border-border rounded-lg p-4 cursor-pointer hover:bg-secondary/40 transition-colors">
                <div className="flex items-center gap-3">
                  <RadioGroupItem value={m.key} data-testid={`pay-${m.key}`} />
                  <div className="flex flex-col">
                    <span className="font-medium text-sm">{t(`payments.${m.key}`)}</span>
                    {m.hint && <span className="text-[11px] text-muted-foreground mt-0.5">{m.hint}</span>}
                  </div>
                </div>
                <span className="text-sm text-muted-foreground tabular-nums">{m.price}</span>
              </label>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Schedule */}
      <Card className="mb-6">
        <CardHeader><CardTitle className="font-display tracking-tight text-2xl">{t("parent.chooseSchedule")}</CardTitle></CardHeader>
        <CardContent>
          <Label className="text-sm">Preferred timezone</Label>
          <select data-testid="tz-select" value={tz} onChange={(e) => setTz(e.target.value)} className="w-full border border-input rounded-md p-2 mt-1 text-sm bg-card">
            <option>Europe/Paris</option><option>Europe/London</option><option>America/New_York</option><option>America/Los_Angeles</option><option>Asia/Tokyo</option><option>Asia/Dubai</option><option>Africa/Casablanca</option>
          </select>
        </CardContent>
      </Card>

      {/* Financial breakdown */}
      <Card className="mb-6 border-accent/40" data-testid="financial-breakdown">
        <CardHeader>
          <CardTitle className="font-display tracking-tight text-2xl">{lang === "fr" ? "Récapitulatif financier" : "Financial breakdown"}</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {lang === "fr"
              ? `${courseCount} cours inscrit${courseCount > 1 ? "s" : ""} · ${fmt(annualPrice)} € / cours / an`
              : `${courseCount} enrolled course${courseCount > 1 ? "s" : ""} · €${fmt(annualPrice)} / course / year`}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-xl bg-accent/10 p-4 flex items-start justify-between gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-accent mb-1">
                {lang === "fr" ? "À régler maintenant — Acompte" : "Due now — Deposit"}
              </div>
              <div className="text-sm">
                {lang === "fr"
                  ? (courseCount <= 1
                      ? `Acompte forfaitaire pour 1 cours, non-remboursable, déduit du total annuel`
                      : `Acompte forfaitaire pour ${courseCount} cours, non-remboursable, déduit du total annuel`)
                  : (courseCount <= 1
                      ? `Flat deposit for 1 course, non-refundable, deducted from yearly total`
                      : `Flat deposit for ${courseCount} courses, non-refundable, deducted from yearly total`)}
              </div>
            </div>
            <div data-testid="deposit-amount" className="text-3xl font-display font-bold text-accent tabular-nums whitespace-nowrap">
              {fmt(deposit)} €
            </div>
          </div>
          <div className="rounded-xl border border-border p-4 flex items-start justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                {lang === "fr" ? "Solde restant à payer" : "Remaining balance"}
              </div>
              <div className="text-sm">
                {fmt(balance)} €&nbsp;
                <span className="text-muted-foreground text-xs">
                  ({fmt(annualTotal)} € − {fmt(deposit)} €)
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {lang === "fr"
                  ? `Selon le plan choisi : ${installmentCount} × ${fmt(Math.round(balanceInstallment * 100) / 100)} €`
                  : `Per chosen plan: ${installmentCount} × €${fmt(Math.round(balanceInstallment * 100) / 100)}`}
              </div>
            </div>
            <div className="text-lg font-display font-semibold tabular-nums whitespace-nowrap" data-testid="balance-amount">
              {fmt(balance)} €
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground pt-1 leading-relaxed">
            {lang === "fr"
              ? "Le solde sera prélevé selon le plan de paiement choisi ci-dessus. Toutes les transactions sont sécurisées par Stripe."
              : "The balance will be charged per the payment plan selected above. All transactions are secured by Stripe."}
          </div>
        </CardContent>
      </Card>

      <Button data-testid="confirm-enrollment" disabled={submitting || !signed || (catalog?.eligible && selectedCourseIds.length < 1)} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-7 shadow-md" onClick={submit}>
        {submitting
          ? t("common.loading")
          : (lang === "fr"
              ? `Signer & verser l'acompte (${fmt(deposit)} €) →`
              : `Sign & pay deposit (€${fmt(deposit)}) →`)}
      </Button>
      <p className="text-xs text-muted-foreground mt-3">
        {lang === "fr"
          ? "Paiement sécurisé via Stripe. Vous serez redirigé pour saisir vos coordonnées bancaires, puis ramené ici une fois le paiement validé."
          : "Secure payment via Stripe. You will be redirected to enter card details, then returned here once payment is confirmed."}
      </p>
    </div>
  );
}
